import { useState, useEffect } from 'react';
import { getStaff, createStaff, resetPin, revokeStaff } from '../api';

const s = {
  page: { minHeight: '100vh', backgroundColor: '#f5f5f3', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' },
  topNav: { backgroundColor: '#fff', borderBottom: '0.5px solid #e8e8e8', padding: '0 32px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  navBrand: { fontSize: '15px', fontWeight: '700', color: '#1a1a1a', letterSpacing: '-0.3px' },
  navRight: { display: 'flex', alignItems: 'center', gap: '20px' },
  navLink: { fontSize: '13px', color: '#666', textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  subNav: { backgroundColor: '#fff', borderBottom: '0.5px solid #e8e8e8', padding: '0 32px', display: 'flex' },
  subNavBtn: { padding: '12px 16px', border: 'none', borderBottom: '2px solid transparent', backgroundColor: 'transparent', fontSize: '13px', color: '#aaa', cursor: 'pointer', fontWeight: '500' },
  subNavBtnActive: { color: '#1a1a1a', borderBottomColor: '#1a1a1a' },
  content: { maxWidth: '900px', margin: '0 auto', padding: '32px 24px' },
  card: { backgroundColor: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '8px', padding: '24px', marginBottom: '20px' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  cardTitle: { fontSize: '15px', fontWeight: '600', color: '#1a1a1a' },
  error: { backgroundColor: '#fdecea', color: '#c0392b', padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', fontSize: '13px' },
  createRow: { display: 'flex', gap: '10px', marginBottom: '20px' },
  input: { flex: 1, padding: '9px 12px', border: '0.5px solid #e0e0e0', borderRadius: '6px', fontSize: '13px', outline: 'none', color: '#1a1a1a' },
  btnPrimary: { padding: '9px 18px', backgroundColor: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '0.5px solid #e8e8e8' },
  td: { padding: '14px 12px', color: '#1a1a1a', borderBottom: '0.5px solid #f0f0f0', verticalAlign: 'middle' },
  tdActions: { padding: '14px 12px', borderBottom: '0.5px solid #f0f0f0', display: 'flex', gap: '6px', alignItems: 'center' },
  badgeActive: { fontSize: '11px', backgroundColor: '#f0faf4', color: '#2e7d32', padding: '3px 10px', borderRadius: '20px', fontWeight: '500', border: '0.5px solid #c8e6c9' },
  badgeInactive: { fontSize: '11px', backgroundColor: '#fdecea', color: '#c0392b', padding: '3px 10px', borderRadius: '20px', fontWeight: '500', border: '0.5px solid #f5c6c6' },
  actionBtn: { padding: '5px 12px', backgroundColor: '#fff', color: '#444', border: '0.5px solid #e0e0e0', borderRadius: '5px', fontSize: '12px', cursor: 'pointer' },
  actionBtnDanger: { padding: '5px 12px', backgroundColor: '#fff', color: '#c0392b', border: '0.5px solid #f5c6c6', borderRadius: '5px', fontSize: '12px', cursor: 'pointer' },
  pinBox: { backgroundColor: '#f0faf4', border: '0.5px solid #c8e6c9', borderRadius: '6px', padding: '16px', marginBottom: '20px' },
  pinCode: { fontSize: '28px', fontWeight: '700', letterSpacing: '6px', color: '#1a1a1a', margin: '8px 0' },
  muted: { fontSize: '13px', color: '#aaa' },
  hint: { fontSize: '12px', color: '#888', marginBottom: '16px' },
};

function Staff({ onBack, onNavigate }) {
  const restaurant = JSON.parse(localStorage.getItem('restaurant') || '{}');
  const [staff, setStaff] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newPin, setNewPin] = useState(null);
  const [newPinUser, setNewPinUser] = useState('');

  useEffect(() => { fetchStaff(); }, []);

  const fetchStaff = async () => {
    try {
      const res = await getStaff();
      setStaff(res.data);
    } catch {
      setError('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newUsername.trim()) return;
    setError('');
    setNewPin(null);
    try {
      const res = await createStaff({ username: newUsername.trim() });
      setStaff([...staff, res.data]);
      setNewUsername('');
      setNewPin(res.data.pin);
      setNewPinUser(res.data.username);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create staff');
    }
  };

  const handleResetPin = async (id, username) => {
    setError('');
    setNewPin(null);
    try {
      const res = await resetPin(id);
      setNewPin(res.data.pin);
      setNewPinUser(username);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset PIN');
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke access for this staff member?')) return;
    setError('');
    try {
      await revokeStaff(id);
      setStaff(staff.map(m => m.id === id ? { ...m, is_active: false } : m));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke access');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('restaurant');
    window.location.href = '/';
  };

  const nav = (tab) => { if (onNavigate) onNavigate(tab); else onBack(); };

  return (
    <div style={s.page}>
      <div style={s.topNav}>
        <span style={s.navBrand}>{restaurant.name || 'Menu Board'}</span>
        <div style={s.navRight}>
          <a href={'/menu/' + restaurant.slug} target="_blank" rel="noreferrer" style={s.navLink}>View menu</a>
          <button style={s.navLink} onClick={handleLogout}>Sign out</button>
        </div>
      </div>
            <div style={s.subNav}>
        <button style={{...s.subNavBtn, ...('menus' === 'staff' ? s.subNavBtnActive : {})}} onClick={() => nav('menus')}>Menus</button>
        <button style={{...s.subNavBtn, ...('hours' === 'staff' ? s.subNavBtnActive : {})}} onClick={() => nav('hours')}>Hours</button>
        <button style={{...s.subNavBtn, ...('profile' === 'staff' ? s.subNavBtnActive : {})}} onClick={() => nav('profile')}>Profile</button>
        <button style={{...s.subNavBtn, ...('staff' === 'staff' ? s.subNavBtnActive : {})}} onClick={() => nav('staff')}>Staff</button>
        <button style={{...s.subNavBtn, ...('settings' === 'staff' ? s.subNavBtnActive : {})}} onClick={() => nav('settings')}>Settings</button>
      </div>

      <div style={s.content}>
        {error && <p style={s.error}>{error}</p>}

        {newPin && (
          <div style={s.pinBox}>
            <p style={{ fontSize: '13px', color: '#1a1a1a', marginBottom: '4px' }}>
              Account created for <strong>{newPinUser}</strong>. Share this PIN — shown once only:
            </p>
            <p style={s.pinCode}>{newPin}</p>
            <p style={{ fontSize: '12px', color: '#888' }}>
              Staff login: {window.location.origin}/staff
            </p>
          </div>
        )}

        <div style={s.card}>
          <div style={s.cardHeader}>
            <h2 style={s.cardTitle}>Staff Accounts</h2>
          </div>
          <p style={s.hint}>Staff can log in with their username and PIN to mark items as sold out.</p>

          <form onSubmit={handleCreate} style={s.createRow}>
            <input
              style={s.input}
              type="text"
              placeholder="Username e.g. waiter_1"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
            />
            <button style={s.btnPrimary} type="submit">Create Staff</button>
          </form>

          {loading ? (
            <p style={s.muted}>Loading...</p>
          ) : staff.length === 0 ? (
            <p style={s.muted}>No staff accounts yet.</p>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Username</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((member, i) => (
                  <tr key={member.id} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={s.td}>{member.username}</td>
                    <td style={s.td}>
                      <span style={member.is_active ? s.badgeActive : s.badgeInactive}>
                        {member.is_active ? 'Active' : 'Revoked'}
                      </span>
                    </td>
                    <td style={s.tdActions}>
                      <button style={s.actionBtn} onClick={() => handleResetPin(member.id, member.username)}>
                        Reset PIN
                      </button>
                      {member.is_active && (
                        <button style={s.actionBtnDanger} onClick={() => handleRevoke(member.id)}>
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default Staff;
